import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { scrollToElement, getBannerHeight, getHeaderHeight, getTotalFixedHeight } from "../utils/scrollUtils";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const previousHashRef = useRef(hash);
  const previousPathnameRef = useRef(pathname);

  // Update CSS variables for banner, header, and total fixed height on mount and resize
  useEffect(() => {
    const updateFixedHeights = () => {
      const bannerHeight = getBannerHeight();
      const headerHeight = getHeaderHeight();
      const totalFixedHeight = getTotalFixedHeight();
      
      document.documentElement.style.setProperty('--banner-height', `${bannerHeight}px`);
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      document.documentElement.style.setProperty('--total-fixed-height', `${totalFixedHeight}px`);
    };

    updateFixedHeights();
    window.addEventListener('resize', updateFixedHeights);

    // Keep header offset in sync when banner content/height changes (rotating cards)
    const banner = document.querySelector('[aria-label="Rotating promotional cards"]');
    let resizeObserver;
    if (banner && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateFixedHeights());
      resizeObserver.observe(banner);
    }

    const timeoutId = setTimeout(updateFixedHeights, 100);
    const timeoutId2 = setTimeout(updateFixedHeights, 400);

    return () => {
      window.removeEventListener('resize', updateFixedHeights);
      resizeObserver?.disconnect();
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
    };
  }, []);

  useEffect(() => {
    // Check if pathname changed
    const pathnameChanged = previousPathnameRef.current !== pathname;
    previousPathnameRef.current = pathname;

    // Update fixed heights when pathname changes
    if (pathnameChanged) {
      const bannerHeight = getBannerHeight();
      const headerHeight = getHeaderHeight();
      const totalFixedHeight = getTotalFixedHeight();
      
      document.documentElement.style.setProperty('--banner-height', `${bannerHeight}px`);
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      document.documentElement.style.setProperty('--total-fixed-height', `${totalFixedHeight}px`);
    }

    if (hash) {
      // Handle hash-based scrolling to sections with robust retry logic
      const scrollToHash = async () => {
        return await scrollToElement(hash, { 
          smooth: true,
          maxRetries: 15,
          retryDelay: 150
        });
      };

      // If pathname changed and there's a hash, first scroll to top, then to hash
      if (pathnameChanged) {
        // First, scroll to top immediately to reset position
        window.scrollTo(0, 0);
        
        // Wait for next render cycle, then attempt to scroll
        const rafId = requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Start scrolling with retry logic built into scrollToElement
            scrollToHash().catch(() => {
              // Silent fail - scrollToElement handles retries internally
            });
          });
        });

        previousHashRef.current = hash;

        return () => {
          cancelAnimationFrame(rafId);
        };
      } else if (previousHashRef.current !== hash) {
        // Hash changed on same page (e.g., clicking different anchor links)
        // Small delay to ensure DOM is ready, then scroll
        const timeoutId = setTimeout(() => {
          scrollToHash().catch(() => {
            // Silent fail - scrollToElement handles retries internally
          });
        }, 100);

        previousHashRef.current = hash;

        return () => {
          clearTimeout(timeoutId);
        };
      } else {
        // Hash is the same but might be a re-click, still scroll
        const timeoutId = setTimeout(() => {
          scrollToHash().catch(() => {
            // Silent fail - scrollToElement handles retries internally
          });
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    } else {
      // Scroll to top when there's no hash
      // Always scroll to top on pathname change, or if hash was removed
      if (pathnameChanged || previousHashRef.current) {
        window.scrollTo(0, 0);
      }
      previousHashRef.current = null;
    }
  }, [pathname, hash]);

  // Also listen for hash changes via popstate (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const currentHash = window.location.hash;
      if (currentHash) {
        scrollToElement(currentHash, { 
          smooth: true,
          maxRetries: 15,
          retryDelay: 150
        }).catch(() => {
          // Silent fail - scrollToElement handles retries internally
        });
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  return null;
};

export default ScrollToTop;